import React, { useState, useEffect } from "react";
import { Card, Tabs, Typography, Space, Row, Col, Button, Tag } from "antd";

const { Title, Paragraph, Text } = Typography;

const ReinforcementLearning = () => {
  const [gameState, setGameState] = useState({
    position: { x: 0, y: 4 },
    score: 0,
    steps: 0,
    gameOver: false,
    history: [],
  });
  const [isTraining, setIsTraining] = useState(false);
  const [qTable, setQTable] = useState({});
  const [episode, setEpisode] = useState(0);

  // Grid setup
  const gridSize = 5;
  const grid = [
    ["⬜", "⬜", "⬜", "⬜", "🏆"],
    ["⬜", "🚧", "⬜", "🚧", "⬜"],
    ["⬜", "💎", "⬜", "💎", "⬜"],
    ["💎", "🚧", "⬜", "⬜", "⬜"],
    ["⬜", "⬜", "⬜", "⬜", "⬜"],
  ];

  const rewards = {
    "🏆": 100,
    "💎": 15,
    "🚧": -10,
    "⬜": -1,
  };

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.currentTrainingInterval) {
        clearInterval(window.currentTrainingInterval);
        window.currentTrainingInterval = null;
      }
    };
  }, []);

  const resetGame = () => {
    if (typeof window !== "undefined" && window.currentTrainingInterval) {
      clearInterval(window.currentTrainingInterval);
      window.currentTrainingInterval = null;
    }
    setGameState({
      position: { x: 0, y: 4 },
      score: 0,
      steps: 0,
      gameOver: false,
      history: [],
    });
    setIsTraining(false);
    setQTable({});
    setEpisode(0);
  };

  const moveAgent = (direction) => {
    if (gameState.gameOver || isTraining) return;

    setGameState((prev) => {
      let newX = prev.position.x;
      let newY = prev.position.y;

      if (direction === "up" && newY > 0) newY--;
      if (direction === "down" && newY < gridSize - 1) newY++;
      if (direction === "left" && newX > 0) newX--;
      if (direction === "right" && newX < gridSize - 1) newX++;

      const cell = grid[newY][newX];
      const reward = rewards[cell] || 0;
      const newScore = prev.score + reward;
      const gameOver = cell === "🏆";

      const newHistory = [
        ...prev.history,
        {
          action: direction,
          cell: cell,
          reward: reward,
          position: { x: newX, y: newY },
        },
      ].slice(-5);

      return {
        position: { x: newX, y: newY },
        score: newScore,
        steps: prev.steps + 1,
        gameOver: gameOver,
        history: newHistory,
      };
    });
  };

  const trainAI = () => {
    setIsTraining(true);
    let currentEpisode = 0;
    const maxEpisodes = 100;
    let newQTable = {};
    let episodeInterval = null;

    for (let y = 0; y < gridSize; y++) {
      for (let x = 0; x < gridSize; x++) {
        newQTable[`${x},${y}`] = { up: 0, down: 0, left: 0, right: 0 };
      }
    }

    const runEpisode = () => {
      let x = 0,
        y = 4;
      let steps = 0;
      let episodeScore = 0;
      const maxSteps = 100;
      const learningRate = 0.1;
      const discountFactor = 0.95;
      const epsilon = Math.max(0.05, 1 - currentEpisode / maxEpisodes);

      episodeInterval = setInterval(() => {
        if (steps >= maxSteps || grid[y][x] === "🏆") {
          clearInterval(episodeInterval);

          if (grid[y][x] === "🏆") {
            setGameState((prev) => ({ ...prev, gameOver: true }));
          }

          currentEpisode++;
          setEpisode(currentEpisode);

          if (currentEpisode < maxEpisodes && isTraining) {
            setTimeout(runEpisode, 50);
          } else {
            setIsTraining(false);
            setQTable(newQTable);
          }
          return;
        }

        let action;
        const state = `${x},${y}`;

        if (Math.random() < epsilon) {
          const actions = ["up", "down", "left", "right"];
          action = actions[Math.floor(Math.random() * actions.length)];
        } else {
          const qValues = newQTable[state];
          action = Object.keys(qValues).reduce((a, b) =>
            qValues[a] > qValues[b] ? a : b
          );
        }

        let newX = x,
          newY = y;
        if (action === "up" && y > 0) newY--;
        if (action === "down" && y < gridSize - 1) newY++;
        if (action === "left" && x > 0) newX--;
        if (action === "right" && x < gridSize - 1) newX++;

        const newState = `${newX},${newY}`;
        const reward = rewards[grid[newY][newX]] || 0;
        episodeScore += reward;

        const currentQ = newQTable[state][action];
        const maxFutureQ = Math.max(...Object.values(newQTable[newState]));
        newQTable[state][action] =
          currentQ +
          learningRate * (reward + discountFactor * maxFutureQ - currentQ);

        setGameState({
          position: { x: newX, y: newY },
          score: episodeScore,
          steps: steps,
          gameOver: false,
          history: [],
        });

        x = newX;
        y = newY;
        steps++;
      }, 150);

      if (typeof window !== "undefined") {
        window.currentTrainingInterval = episodeInterval;
      }
    };

    runEpisode();
  };

  const stopTraining = () => {
    if (typeof window !== "undefined" && window.currentTrainingInterval) {
      clearInterval(window.currentTrainingInterval);
      window.currentTrainingInterval = null;
    }
    setIsTraining(false);
    setGameState((prev) => ({
      ...prev,
      position: { x: 0, y: 4 },
      gameOver: false,
    }));
  };

  const renderConcept = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            What is Reinforcement Learning?
          </span>
        }
      >
        <Paragraph style={{ fontSize: "16px" }}>
          Imagine learning to ride a bike. You don't read a manual - you try,
          fall, adjust, and try again. Each time you fall (negative feedback) or
          stay balanced (positive feedback), you learn what works and what
          doesn't.
        </Paragraph>
        <Paragraph style={{ fontSize: "16px", marginBottom: 0 }}>
          Reinforcement Learning (RL) is exactly how AI learns! An agent tries
          different actions, gets rewards or penalties, and gradually figures
          out the best strategy through experience.
        </Paragraph>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            The Reinforcement Learning Loop
          </span>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={12} md={6}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  // background: "#0071e3",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "48px",
                  margin: "0 auto 12px",
                }}
              >
                🤖
              </div>
              <Text strong>Agent</Text>
              <div style={{ fontSize: "12px", color: "#666" }}>The learner</div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  // background: "#52c41a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "48px",
                  margin: "0 auto 12px",
                }}
              >
                🎯
              </div>
              <Text strong>Action</Text>
              <div style={{ fontSize: "12px", color: "#666" }}>
                What it does
              </div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  // background: "#faad14",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "48px",
                  margin: "0 auto 12px",
                }}
              >
                🌍
              </div>
              <Text strong>Environment</Text>
              <div style={{ fontSize: "12px", color: "#666" }}>The world</div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  // background: "#ff4d4f",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "48px",
                  margin: "0 auto 12px",
                }}
              >
                ⭐
              </div>
              <Text strong>Reward</Text>
              <div style={{ fontSize: "12px", color: "#666" }}>Feedback</div>
            </div>
          </Col>
        </Row>

        <div
          style={{
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid #0071e3",
          }}
        >
          <Paragraph style={{ fontWeight: 600, marginBottom: "8px" }}>
            The Loop
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            Agent observes environment → Takes action → Gets reward/penalty →
            Learns which actions work best → Repeats thousands of times →
            Becomes expert!
          </Paragraph>
        </div>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Key Concepts</span>}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {[
            {
              title: "Agent",
              desc: "The AI that makes decisions and learns. Like you when learning to play chess.",
            },
            {
              title: "Environment",
              desc: "The world the agent interacts with. Like the chess board with pieces.",
            },
            {
              title: "Actions",
              desc: "Possible moves the agent can make. Like moving chess pieces.",
            },
            {
              title: "Rewards",
              desc: "Feedback signals: +100 for winning, -10 for losing a piece, +1 for capturing.",
            },
            {
              title: "Policy",
              desc: "The agent's strategy - what action to take in each situation.",
            },
            {
              title: "Exploration vs Exploitation",
              desc: "Try new moves (explore) or use known good moves (exploit). Finding balance is key!",
            },
          ].map((concept, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                borderRadius: "8px",
              }}
            >
              <div>
                <div style={{ fontWeight: 600, marginBottom: "2px" }}>
                  {concept.title}
                </div>
                <div style={{ fontSize: "14px", color: "#666" }}>
                  {concept.desc}
                </div>
              </div>
            </div>
          ))}
        </Space>
      </Card>

      <Card
        title={<span style={{ color: "#0071e3" }}>Think of it like...</span>}
      >
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>Agent:</Text> Your dog
          <br />
          <Text strong>Environment:</Text> Your home/yard
          <br />
          <Text strong>Actions:</Text> Sit, stay, roll over, fetch
          <br />
          <Text strong>Rewards:</Text> Treats when they do it right. No treats
          when they do it wrong.
          <br />
          <Text strong>Learning:</Text> Over time, dog learns which actions get
          treats
          <br />
          <br />
          After many repetitions, your dog masters the tricks. That's
          reinforcement learning!
        </Paragraph>
      </Card>
    </Space>
  );

  const renderDemo = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>Interactive Treasure Hunt</span>
        }
      >
        <Paragraph>
          Help the robot find the treasure! You can play manually, or watch AI
          learn through reinforcement learning.
        </Paragraph>

        <Row gutter={[12, 12]} style={{ marginBottom: "24px" }}>
          <Col xs={isTraining ? 8 : 12}>
            <Card
              size="small"
              style={{ textAlign: "center", borderColor: "#0071e3" }}
            >
              <div
                style={{ fontSize: "24px", fontWeight: 700, color: "#0071e3" }}
              >
                {gameState.score}
              </div>
              <div style={{ fontSize: "12px" }}>Score</div>
            </Card>
          </Col>
          <Col xs={isTraining ? 8 : 12}>
            <Card
              size="small"
              style={{ textAlign: "center", borderColor: "#52c41a" }}
            >
              <div
                style={{ fontSize: "24px", fontWeight: 700, color: "#52c41a" }}
              >
                {gameState.steps}
              </div>
              <div style={{ fontSize: "12px" }}>Steps</div>
            </Card>
          </Col>
          <Col xs={8}>
            {isTraining && (
              <Card
                size="small"
                style={{ textAlign: "center", borderColor: "#faad14" }}
              >
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: 700,
                    color: "#faad14",
                  }}
                >
                  {episode}
                </div>
                <div style={{ fontSize: "12px" }}>Episode</div>
              </Card>
            )}
          </Col>
        </Row>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              display: "inline-block",
              padding: "20px",
              background: "#fafafa",
              borderRadius: "12px",
            }}
          >
            {grid.map((row, y) => (
              <div
                key={y}
                style={{ display: "flex", gap: "4px", marginBottom: "4px" }}
              >
                {row.map((cell, x) => {
                  const isAgent =
                    gameState.position.x === x && gameState.position.y === y;
                  return (
                    <div
                      key={x}
                      style={{
                        width: "60px",
                        height: "60px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "32px",
                        background: isAgent ? "#e6f7ff" : "white",
                        border: isAgent
                          ? "3px solid #0071e3"
                          : "2px solid #d9d9d9",
                        borderRadius: "8px",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {isAgent ? "🤖" : cell}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid #0071e3",
          }}
        >
          <Paragraph style={{ fontWeight: 600, marginBottom: "16px" }}>
            Legend
          </Paragraph>
          <Space wrap>
            <Tag>🤖 Agent</Tag>
            <Tag color="blue">🏆 Goal (+100)</Tag>
            <Tag color="green">💎 Bonus (+15)</Tag>
            <Tag color="red">🚧 Obstacle (-10)</Tag>
            <Tag color="default">⬜ Empty (-1)</Tag>
          </Space>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "24px",
            marginBottom: "24px",
          }}
        >
          <Space wrap align="center">
            {!isTraining && !gameState.gameOver && (
              <>
                <Button onClick={() => moveAgent("up")}>⬆️ Up</Button>
                <Button onClick={() => moveAgent("down")}>⬇️ Down</Button>
                <Button onClick={() => moveAgent("left")}>⬅️ Left</Button>
                <Button onClick={() => moveAgent("right")}>➡️ Right</Button>
              </>
            )}
            {!isTraining ? (
              <Button type="primary" onClick={trainAI}>
                🤖 Train AI
              </Button>
            ) : (
              <Button danger onClick={stopTraining}>
                ⏸️ Stop Training
              </Button>
            )}
            <Button onClick={resetGame}>🔄 Reset</Button>
          </Space>
        </div>

        {gameState.gameOver && (
          <Card
            style={{
              backgroundColor: "#f6ffed",
              borderColor: "#52c41a",
              borderWidth: "2px",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <Title level={4} style={{ color: "#52c41a" }}>
                {isTraining ? "AI Found the Treasure!" : "Treasure Found!"}
              </Title>
              <Paragraph>
                Final Score: {gameState.score} | Steps: {gameState.steps}
              </Paragraph>
              {isTraining && <Text>Training Episode {episode} of 100</Text>}
            </div>
          </Card>
        )}
      </Card>
    </Space>
  );

  const renderHistory = () => {
    const timeline = [
      {
        year: "1950s",
        title: "Early Ideas: Trial and Error Learning",
        event:
          "Psychologists studied how animals learn through rewards and punishments. B.F. Skinner's experiments with pigeons and rats showed that behavior could be shaped by consequences - the foundation of RL!",
      },
      {
        year: "1980s",
        title: "The Birth of Modern RL",
        event:
          "Richard Sutton and Andrew Barto formalized reinforcement learning as a computational framework. They introduced key concepts like temporal difference learning and the exploration-exploitation tradeoff. Their textbook became the RL bible.",
      },
      {
        year: "1992",
        title: "TD-Gammon: RL Plays Backgammon",
        event:
          "Gerald Tesauro created TD-Gammon, an RL agent that learned to play backgammon at expert level by playing against itself millions of times. It discovered strategies humans never thought of! This proved RL could master complex games.",
      },
      {
        year: "2013",
        title: "DeepMind's Deep Q-Learning (DQN)",
        event:
          "DeepMind combined deep learning with Q-learning to create DQN, which learned to play Atari games from raw pixels - no human knowledge needed! It mastered 49 different games using the same algorithm. This breakthrough started the 'deep RL' revolution.",
      },
      {
        year: "2016",
        title: "AlphaGo Defeats World Champion",
        event:
          "DeepMind's AlphaGo used reinforcement learning (plus supervised learning) to defeat Lee Sedol, the world Go champion. Go was thought to be decades away from AI mastery due to its complexity. AlphaGo's move 37 in game 2 stunned experts - a creative move no human would make!",
      },
      {
        year: "2017",
        title: "AlphaZero: Self-Taught Superhuman",
        event:
          "AlphaZero learned chess, shogi, and Go from scratch - no human knowledge, just the rules and self-play. In 4 hours of training, it defeated the world's best chess programs. In 24 hours, it surpassed centuries of human chess knowledge. Pure RL!",
      },
      {
        year: "2019",
        title: "OpenAI Five: Mastering Dota 2",
        event:
          "OpenAI trained five RL agents to beat world champion Dota 2 players. Dota 2 is incredibly complex - team strategy, long-term planning, imperfect information. The agents trained for 10 months of game-time per day using massive compute.",
      },
      {
        year: "2020-Present",
        title: "RL in the Real World",
        event:
          "RL is now powering self-driving cars (Waymo, Tesla), optimizing data centers (Google reduced cooling costs 40%), controlling robots, personalizing recommendations, and even helping design drugs. ChatGPT uses RLHF (Reinforcement Learning from Human Feedback) to follow instructions better!",
      },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {timeline.map((item, idx) => (
          <Card
            key={idx}
            title={
              <span style={{ color: "#0071e3" }}>
                {item.year} - {item.title}
              </span>
            }
          >
            <Paragraph style={{ marginBottom: 0 }}>{item.event}</Paragraph>
          </Card>
        ))}

        <Card title={<span style={{ color: "#0071e3" }}>Quick Facts</span>}>
          <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
            <li>
              AlphaZero discovered chess strategies in 4 hours that humans took
              1,500 years to develop
            </li>
            <li>
              OpenAI Five played 180 years worth of Dota 2 games PER DAY during
              training
            </li>
            <li>
              RL agents can now learn to walk, run, and do backflips in
              simulation - then transfer to real robots
            </li>
            <li>
              Google's RL system saved them millions by optimizing data center
              cooling
            </li>
            <li>
              ChatGPT's "helpfulness" comes from RLHF - humans rating responses
              teaches it what's good!
            </li>
          </ul>
        </Card>
      </Space>
    );
  };

  const renderUses = () => {
    const uses = [
      {
        category: "Gaming & Entertainment",
        examples: [
          "AlphaGo, AlphaZero - mastering board games",
          "OpenAI Five - defeating pro Dota 2 teams",
          "Game AI - smarter NPCs in video games",
          "Poker bots - beating professional players",
        ],
        impact: "RL agents now exceed human performance in many complex games",
      },
      {
        category: "Robotics",
        examples: [
          "Robot arms learning to manipulate objects",
          "Warehouse robots optimizing paths",
          "Drone navigation and control",
          "Humanoid robots learning to walk and balance",
        ],
        impact:
          "Enables robots to learn tasks through practice instead of programming",
      },
      {
        category: "Self-Driving Cars",
        examples: [
          "Waymo - autonomous taxi service",
          "Tesla Autopilot - lane keeping and navigation",
          "Path planning in complex traffic",
          "Parking assistance systems",
        ],
        impact:
          "RL helps cars learn safe driving from millions of simulated scenarios",
      },
      {
        category: "Healthcare",
        examples: [
          "Personalized treatment recommendations",
          "Drug discovery and molecular design",
          "Optimizing radiation therapy for cancer",
          "ICU patient monitoring and alerts",
        ],
        impact: "Can discover treatment strategies humans might miss",
      },
      {
        category: "Business & Finance",
        examples: [
          "Stock trading algorithms",
          "Dynamic pricing (airlines, rideshare)",
          "Recommendation systems (Netflix, YouTube)",
          "Supply chain optimization",
        ],
        impact:
          "Maximizes profits by adapting to changing markets in real-time",
      },
      {
        category: "Energy & Infrastructure",
        examples: [
          "Google data center cooling (40% energy savings)",
          "Smart grid management",
          "Wind turbine control",
          "Traffic light optimization",
        ],
        impact: "Reduces energy costs and environmental impact significantly",
      },
      {
        category: "AI Assistants",
        examples: [
          "ChatGPT - RLHF makes it helpful",
          "Claude - constitutional AI with RL",
          "Dialogue systems that improve over time",
          "Personal assistant scheduling",
        ],
        impact: "RLHF teaches AI to be helpful, harmless, and honest",
      },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card
          title={<span style={{ color: "#0071e3" }}>RL is everywhere</span>}
        >
          <Paragraph style={{ marginBottom: 0 }}>
            From the AI that helps write your emails to self-driving cars to
            optimizing Google's massive data centers. It's one of the most
            powerful and practical AI techniques.
          </Paragraph>
        </Card>

        {uses.map((use, idx) => (
          <Card
            key={idx}
            title={<span style={{ color: "#0071e3" }}>{use.category}</span>}
          >
            <ul style={{ paddingLeft: "20px", marginBottom: "16px" }}>
              {use.examples.map((example, i) => (
                <li key={i}>{example}</li>
              ))}
            </ul>
            <Card
              size="small"
              style={{ border: "1px solid #0071e3", borderRadius: "8px" }}
            >
              <Text strong>Impact:</Text> {use.impact}
            </Card>
          </Card>
        ))}

        <Card
          title={<span style={{ color: "#0071e3" }}>The Future of RL</span>}
        >
          <Paragraph style={{ marginBottom: 0 }}>
            RL is evolving rapidly! Future applications may include personalized
            education (AI tutors that adapt to you), climate modeling and
            optimization, protein folding for drug discovery, general-purpose
            robots in homes, and even scientific research where AI discovers new
            knowledge by experimenting. The combination of RL with large
            language models (like GPT) is creating AI that can plan, reason,
            and take actions in the real world. I guess we are just getting started!
          </Paragraph>
        </Card>
      </Space>
    );
  };

  const tabItems = [
    {
      key: "concept",
      label: "What is RL?",
      children: renderConcept(),
    },
    {
      key: "demo",
      label: "Interactive Demo",
      children: renderDemo(),
    },
    {
      key: "history",
      label: "History",
      children: renderHistory(),
    },
    {
      key: "uses",
      label: "Real Uses",
      children: renderUses(),
    },
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "32px" }}
        className="fade-in-down"
      >
        <Title level={1}>Reinforcement Learning</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          How AI learns through trial and error - just like you learned to ride
          a bike!
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="concept" items={tabItems} size="large" />
    </div>
  );
};

export default ReinforcementLearning;
